/**
 * Document Title Hook
 *
 * Updates the browser tab title with:
 * - Daily realized PnL from active broker's selected account
 * - Environment indicator (STAGING vs PROD)
 */

import { useEffect } from 'react';
import { useActiveBrokerBalance } from '../store/brokerStore';

/**
 * Detect environment based on hostname and port
 */
function getEnvironment(): 'STAGING' | 'PROD' {
  if (typeof window === 'undefined') return 'PROD';

  const hostname = window.location.hostname;
  const port = window.location.port;

  // Check for staging indicators
  if (hostname.includes('staging')) return 'STAGING';
  if (port === '4002') return 'STAGING';  // Staging frontend port
  if (port === '9647') return 'STAGING';  // Staging backend port

  // Check for production indicators
  if (hostname === 'utt.ginuga.com') return 'PROD';
  if (port === '3002') return 'PROD';  // Production frontend port
  if (port === '9547') return 'PROD';  // Production backend port

  // Default to STAGING for localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // If no specific port, assume staging for safety
    return port === '3002' ? 'PROD' : 'STAGING';
  }

  return 'PROD';
}

/**
 * Format PnL for display in title
 */
function formatPnL(pnl: number): string {
  const sign = pnl >= 0 ? '+' : '';
  const formatted = Math.abs(pnl).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}$${pnl >= 0 ? '' : '-'}${formatted}`;
}

/**
 * Hook to update document title with daily PnL and environment
 * Shows PnL from active broker's selected account only
 */
export function useDocumentTitle(): void {
  const { totalPnL } = useActiveBrokerBalance();

  useEffect(() => {
    const env = getEnvironment();
    const envPrefix = env === 'STAGING' ? '[STG] ' : '';

    // Format: "[STG] +$123.45 | UTT" or "+$123.45 | UTT"
    const pnlStr = formatPnL(totalPnL);
    document.title = `${envPrefix}${pnlStr} | UTT`;

    // Also update favicon color based on PnL (optional enhancement)
    // This could be done with a dynamic SVG favicon in the future

  }, [totalPnL]);
}

/**
 * Get current environment
 */
export function useEnvironment(): 'STAGING' | 'PROD' {
  return getEnvironment();
}

export default useDocumentTitle;
